import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createSpecialty, updateSpecialty } from 'api/Admin/Specialty';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';

import useSpecialtyFormik from '../configs/useSpecialtyFormik';

import SpecialtyForm from './SpecialtyForm';

const { default: Modal } = require('components/shared/Modal/Modal');

const SpecialtyModal = ({ isOpen, handleClose, specialtyData = {}, modalType = 'new' }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [formikData, setFormikData] = useState(specialtyData);
  const createNewSpecialty = useMutation({
    mutationFn: (data) => {
      return createSpecialty(navigate, data);
    },
    onSuccess: async (data) => {
      if (data.code === 0) {
        await queryClient.invalidateQueries(['specialty']);
        showAlert({ title: 'Specialty created successfully', color: 'success' });
        formik.resetForm();
        handleClose();
      } else {
        showAlert({ title: data?.error ?? 'Something went wrong creating admin', color: 'danger' });
      }
    }
  });

  const editSpecialty = useMutation({
    mutationFn: (data) => {
      return updateSpecialty(navigate, { data });
    },
    onSuccess: async (data) => {
      if (data.code === 0) {
        await queryClient.invalidateQueries(['specialty']);
        showAlert({ title: 'Specialty edited successfully', color: 'success' });
        formik.resetForm();
        handleClose();
      } else {
        showAlert({ title: data?.error ?? 'Something went wrong creating admin', color: 'danger' });
      }
    }
  });

  const onSubmit = async (values) => {
    if (modalType == 'new') {
      await createNewSpecialty.mutateAsync(values);
    } else {
      await editSpecialty.mutateAsync(values);
    }
  };

  const formik = useSpecialtyFormik(specialtyData ? formikData : {}, onSubmit);

  useEffect(() => {
    setFormikData(specialtyData);
  }, [isOpen]);

  const closeModal = () => {
    setFormikData(null);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={closeModal}
      slideFromRight
      title={modalType == 'new' ? 'New Specialty' : 'Edit Specialty'}
      footer={
        <div className="flex w-full justify-between gap-4">
          <Button outlined color="neutral" text="Close" onClick={() => {}} />
          <Button outlined color="primary" text="Submit" onClick={formik.handleSubmit} />
        </div>
      }>
      <SpecialtyForm formik={formik} />
    </Modal>
  );
};

export default withErrorBoundary(SpecialtyModal);
